/**
 * Auth store.
 *
 * Tokens live in localStorage so a refresh keeps the session. That trades a
 * little XSS exposure for usability; the mitigation is short-lived access
 * tokens plus server-side refresh rotation and reuse detection.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { authApi } from '@/api/endpoints'
import { configureClient } from '@/api/client'
import type { AuthSession, OnboardingSummary, TokenPair, User } from '@/types/api'

interface AuthState {
  user: User | null
  tokens: TokenPair | null
  onboarding: OnboardingSummary | null
  status: 'idle' | 'authenticating' | 'authenticated' | 'anonymous'

  setSession: (session: AuthSession) => void
  setUser: (user: User) => void
  setOnboarding: (summary: Partial<OnboardingSummary>) => void
  clear: () => void

  login: (email: string, password: string) => Promise<AuthSession>
  register: (email: string, password: string, fullName?: string) => Promise<AuthSession>
  logout: () => Promise<void>
  hydrate: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      onboarding: null,
      status: 'idle',

      setSession: (session) =>
        set({
          user: session.user,
          tokens: session.tokens,
          onboarding: session.onboarding,
          status: 'authenticated',
        }),

      setUser: (user) => set({ user }),

      setOnboarding: (summary) =>
        set((state) => ({
          onboarding: state.onboarding ? { ...state.onboarding, ...summary } : null,
        })),

      clear: () => set({ user: null, tokens: null, onboarding: null, status: 'anonymous' }),

      login: async (email, password) => {
        set({ status: 'authenticating' })
        try {
          const session = await authApi.login({ email, password })
          get().setSession(session)
          return session
        } catch (error) {
          set({ status: 'anonymous' })
          throw error
        }
      },

      register: async (email, password, fullName) => {
        set({ status: 'authenticating' })
        try {
          const session = await authApi.register({ email, password, fullName })
          get().setSession(session)
          return session
        } catch (error) {
          set({ status: 'anonymous' })
          throw error
        }
      },

      logout: async () => {
        const refreshToken = get().tokens?.refreshToken
        if (refreshToken) {
          // Best effort: a failed revoke must not trap the user in a session.
          await authApi.logout(refreshToken).catch(() => undefined)
        }
        get().clear()
      },

      hydrate: async () => {
        if (!get().tokens?.accessToken) {
          set({ status: 'anonymous' })
          return
        }
        try {
          const user = await authApi.me()
          set({ user, status: 'authenticated' })
        } catch {
          get().clear()
        }
      },
    }),
    {
      name: 'job-seeker-auth',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        onboarding: state.onboarding,
      }),
    },
  ),
)

/** Wire the HTTP client to this store. Called once, at startup. */
export function initAuthBridge(): void {
  let inFlightRefresh: Promise<string | null> | null = null

  configureClient({
    getAccessToken: () => useAuthStore.getState().tokens?.accessToken ?? null,

    refreshTokens: async () => {
      // Collapse concurrent 401s into a single refresh, so parallel requests
      // do not each burn a rotation and invalidate one another.
      if (inFlightRefresh) return inFlightRefresh

      const refreshToken = useAuthStore.getState().tokens?.refreshToken
      if (!refreshToken) return null

      inFlightRefresh = (async () => {
        try {
          const tokens = await authApi.refresh(refreshToken)
          useAuthStore.setState({ tokens })
          return tokens.accessToken
        } catch {
          useAuthStore.getState().clear()
          return null
        } finally {
          inFlightRefresh = null
        }
      })()

      return inFlightRefresh
    },

    onUnauthorized: () => {
      useAuthStore.getState().clear()
    },
  })
}
